import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
// TODO: wrap IconButton, MenuItem, Popover, Select, TextField as foundation primitives
import {
  IconButton,
  MenuItem,
  Popover,
  Select,
  TextField,
} from "@mui/material";
import * as React from "react";

import {
  type TimeDisplayMode,
  useWorkspaceTimeBounds,
} from "@/shared/components/visualization/WorkspaceCtx";
import type { CanonicalDataType } from "@/shared/domain/topics";
import { usePopupState } from "@/shared/hooks/usePopupState";
import { TimeBoundsSource } from "@/shared/state/visualization/schema";

import styles from "./ColumnFilterPopover.module.css";
import {
  conditionMeta,
  getConditionsForDataType,
  getFilterPlaceholder,
  isDatetimeColumn,
  isValuelessCondition,
  needsSecondValue,
  shouldUseDatetimePicker,
} from "./conditionMeta";
import { DatetimeFilterInput } from "./DatetimeFilterInput";
import {
  type FilterStageColorVars,
  stageColorStyle,
} from "./filterStageColors";
import { isValidRegex } from "./regexUtils";
import { canCommitLogtime, resolveLogTimeValue } from "./resolveLogTimeValue";
import { seedLogTimeBuffer } from "./seedLogTimeBuffer";
import {
  type FilterCondition,
  type FilterStage,
  type StageCombinator,
} from "./state";

export interface ColumnFilterPopoverProps {
  popup: ReturnType<typeof usePopupState<void>>;
  columnName: string;
  /** Logical column key; keys into colorMap for highlight colors. */
  columnId?: string;
  /** Map from `${columnId}:${stageIndex}` to a stage-color slot. Owned by LogPanel. */
  colorMap: Map<string, number>;
  /** Color variables for each stage slot. Owned by LogPanel. */
  stageColors: FilterStageColorVars[];
  stages: FilterStage[];
  dataType?: CanonicalDataType;
  timeDisplayMode: TimeDisplayMode;
  combinator: StageCombinator;
  onStageChange: (stageIndex: number, stage: FilterStage) => void;
  onStageAdd: (stage: FilterStage) => void;
  onStageDelete: (stageIndex: number) => void;
  onCombinatorChange: (combinator: StageCombinator) => void;
}

/**
 * A popover used to apply one or more filter stages to a log panel column.
 *
 * Each stage has a condition dropdown and, depending on the condition and
 * data type, either a text input, two text inputs (for `between`), or a
 * datetime picker (for timestamp columns in Local/UTC mode). When a
 * timestamp column is in a relative time mode (Log / Elapsed / ElapsedTime),
 * the text input is paired with a confirm button that resolves the typed
 * display-format value to a canonical nanosecond epoch string.
 *
 * Multiple stages are combined with the shared combinator (AND / OR).
 *
 * The popover also contains an "Add condition" button to append new stages,
 * and a delete button on each stage when more than one stage exists.
 */
export function ColumnFilterPopover({
  popup,
  columnName,
  columnId,
  colorMap,
  stageColors,
  stages,
  dataType,
  timeDisplayMode,
  combinator,
  onStageChange,
  onStageAdd,
  onStageDelete,
  onCombinatorChange,
}: ColumnFilterPopoverProps) {
  const availableConditions = getConditionsForDataType(dataType);
  return (
    <Popover
      disableRestoreFocus
      disableAutoFocus
      open={popup.isOpen}
      anchorEl={popup.anchor}
      onClose={() => popup.close()}
      anchorOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          popup.close();
        }}
      >
        <div className={styles.treeRoot}>
          <span className={styles.columnHeader}>{columnName}:</span>
          <div className={styles.stageList}>
            {stages.map((stage, index) => (
              <React.Fragment key={index}>
                {index > 0 && (
                  <div className={styles.combinatorRow}>
                    <div className={styles.traceLine} />
                    <button
                      className={styles.combinatorToggle}
                      onClick={() =>
                        onCombinatorChange(combinator === "or" ? "and" : "or")
                      }
                      type="button"
                      aria-label={`Toggle filter combinator, currently ${combinator === "or" ? "OR" : "AND"}`}
                    >
                      {combinator === "or" ? "OR" : "AND"}
                    </button>
                  </div>
                )}
                <div
                  className={styles.stageEntry}
                  style={
                    columnId !== undefined &&
                    colorMap.has(`${columnId}:${index}`)
                      ? stageColorStyle(
                          colorMap.get(`${columnId}:${index}`)!,
                          stageColors,
                        )
                      : undefined
                  }
                >
                  <div className={styles.traceLine} />
                  <div className={styles.stageCard}>
                    <StageRow
                      stage={stage}
                      stageNumber={index + 1}
                      availableConditions={availableConditions}
                      autoFocusTextField={index === stages.length - 1}
                      showDelete={stages.length > 1}
                      dataType={dataType}
                      timeDisplayMode={timeDisplayMode}
                      onChange={(updated) => onStageChange(index, updated)}
                      onDelete={() => onStageDelete(index)}
                    />
                  </div>
                </div>
              </React.Fragment>
            ))}
            <div className={styles.traceEnd}>
              <div className={styles.traceLineDashed} />
              <IconButton
                size="small"
                title="Add condition"
                aria-label="Add another filter condition"
                onClick={() =>
                  onStageAdd({ value: "", condition: availableConditions[0] })
                }
                className={styles.addButton}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </div>
          </div>
        </div>
      </form>
    </Popover>
  );
}

interface StageRowProps {
  stage: FilterStage;
  stageNumber: number;
  availableConditions: FilterCondition[];
  autoFocusTextField: boolean;
  showDelete: boolean;
  dataType?: CanonicalDataType;
  timeDisplayMode: TimeDisplayMode;
  onChange: (stage: FilterStage) => void;
  onDelete: () => void;
}

function StageRow({
  stage,
  stageNumber,
  availableConditions,
  autoFocusTextField,
  showDelete,
  dataType,
  timeDisplayMode,
  onChange,
  onDelete,
}: StageRowProps) {
  const condition = stage.condition;
  const isRegex = condition === "matches_regex";
  const isNumeric = conditionMeta[condition].numericOnly;
  const hasRegexError =
    isRegex && stage.value !== "" && !isValidRegex(stage.value);
  const hideTextField = isValuelessCondition(condition);
  const showSecondValue = needsSecondValue(condition);
  const useDatetimePicker = shouldUseDatetimePicker(
    dataType,
    timeDisplayMode,
    condition,
  );

  const timeBounds = useWorkspaceTimeBounds({
    timeBoundsSource: TimeBoundsSource.FilesInWorkspace,
  });
  const earliestTime = timeBounds.earliest ?? null;

  // For non-datetime logtime fields, the stored value is a canonical nanosecond
  // epoch string. The user types in display-mode format (e.g. seconds, hh:mm:ss)
  // and confirms with a checkmark button, which resolves to nanoseconds.
  const isLogTimeText =
    !useDatetimePicker && isDatetimeColumn(dataType) && isNumeric;
  const inputType = isLogTimeText || !isNumeric ? "text" : "number";

  // The local buffer is a DISPLAY-format view of the canonical nanosecond
  // `stage.value`. It re-seeds whenever the mode, earliestTime, or canonical
  // value changes so the popover stays in sync with TimeDisplayMode switches
  // and external stage mutations. Any in-flight user edit is discarded on
  // re-seed — the canonical value is the source of truth.
  const [localValue, setLocalValue] = React.useState(() =>
    seedLogTimeBuffer(
      stage.value,
      isLogTimeText,
      timeDisplayMode,
      earliestTime,
    ),
  );
  const [localValue2, setLocalValue2] = React.useState(() =>
    seedLogTimeBuffer(
      stage.value2 ?? "",
      isLogTimeText,
      timeDisplayMode,
      earliestTime,
    ),
  );
  React.useEffect(() => {
    setLocalValue(
      seedLogTimeBuffer(
        stage.value,
        isLogTimeText,
        timeDisplayMode,
        earliestTime,
      ),
    );
    setLocalValue2(
      seedLogTimeBuffer(
        stage.value2 ?? "",
        isLogTimeText,
        timeDisplayMode,
        earliestTime,
      ),
    );
  }, [stage.value, stage.value2, isLogTimeText, timeDisplayMode, earliestTime]);

  return (
    <>
      <div className={styles.selectRow}>
        <Select
          size="small"
          fullWidth
          value={condition}
          aria-label={`Condition for stage ${stageNumber}`}
          onChange={(event) => {
            const nextCondition = event.target.value as FilterCondition;
            if (isValuelessCondition(nextCondition)) {
              onChange({ value: "", condition: nextCondition });
            } else {
              onChange({
                value: stage.value,
                condition: nextCondition,
                ...(needsSecondValue(nextCondition) &&
                  stage.value2 !== undefined && { value2: stage.value2 }),
              });
            }
          }}
        >
          {availableConditions.map((key) => (
            <MenuItem key={key} value={key}>
              {conditionMeta[key].label}
            </MenuItem>
          ))}
        </Select>
        {showDelete && (
          <IconButton
            size="small"
            className={styles.inlineDeleteButton}
            onClick={onDelete}
            title="Remove condition"
            aria-label="Delete filter stage"
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        )}
      </div>
      {useDatetimePicker &&
        (() => {
          return showSecondValue ? (
            <DatetimeFilterInput
              mode="between"
              layout="combined"
              from={stage.value}
              to={stage.value2 ?? ""}
              onChange={(from, to) =>
                onChange({ value: from, condition, value2: to })
              }
              timeDisplayMode={timeDisplayMode}
              earliestTime={earliestTime}
              textFieldProps={{ fullWidth: true, variant: "outlined" }}
              disablePopoverRestoreFocus
            />
          ) : (
            <DatetimeFilterInput
              mode="single"
              value={stage.value}
              onChange={(v) => onChange({ value: v, condition })}
              timeDisplayMode={timeDisplayMode}
              earliestTime={earliestTime}
              textFieldProps={{ fullWidth: true, variant: "outlined" }}
              disablePopoverRestoreFocus
            />
          );
        })()}
      {!useDatetimePicker && !hideTextField && (
        <TextField
          autoFocus={autoFocusTextField}
          fullWidth
          size="small"
          type={inputType}
          placeholder={getFilterPlaceholder(isRegex, showSecondValue)}
          variant="outlined"
          value={isLogTimeText ? localValue : stage.value}
          error={
            hasRegexError ||
            (isLogTimeText &&
              localValue.trim() !== "" &&
              resolveLogTimeValue(localValue, timeDisplayMode, earliestTime) ===
                null)
          }
          onChange={(event) => {
            if (isLogTimeText) {
              setLocalValue(event.target.value);
            } else {
              onChange({
                value: event.target.value,
                condition,
                ...(showSecondValue && { value2: stage.value2 ?? "" }),
              });
            }
          }}
        />
      )}
      {!useDatetimePicker && showSecondValue && (
        <TextField
          fullWidth
          size="small"
          type={inputType}
          placeholder="Max"
          variant="outlined"
          value={isLogTimeText ? localValue2 : stage.value2 ?? ""}
          error={
            isLogTimeText &&
            localValue2.trim() !== "" &&
            resolveLogTimeValue(localValue2, timeDisplayMode, earliestTime) ===
              null
          }
          onChange={(event) => {
            if (isLogTimeText) {
              setLocalValue2(event.target.value);
            } else {
              onChange({
                value: stage.value,
                condition,
                value2: event.target.value,
              });
            }
          }}
        />
      )}
      {isLogTimeText &&
        (() => {
          const resolved = resolveLogTimeValue(
            localValue,
            timeDisplayMode,
            earliestTime,
          );
          const resolved2 = showSecondValue
            ? resolveLogTimeValue(localValue2, timeDisplayMode, earliestTime)
            : undefined;
          const hasError =
            (localValue.trim() !== "" && resolved === null) ||
            (showSecondValue &&
              localValue2.trim() !== "" &&
              resolved2 === null);
          const timeRangeUnavailable = !canCommitLogtime(
            timeDisplayMode,
            earliestTime,
          );
          return (
            <button
              type="button"
              className={styles.confirmButton}
              disabled={
                hasError ||
                localValue.trim() === "" ||
                (showSecondValue && localValue2.trim() === "") ||
                timeRangeUnavailable
              }
              onClick={() => {
                if (resolved === null || timeRangeUnavailable) {
                  return;
                }
                if (showSecondValue && resolved2 === null) {
                  return;
                }
                if (showSecondValue) {
                  onChange({
                    value: resolved,
                    condition,
                    value2: resolved2 ?? "",
                  });
                } else {
                  onChange({ value: resolved, condition });
                }
              }}
              title={
                timeRangeUnavailable
                  ? "Time range unavailable"
                  : "Confirm value"
              }
              aria-label="Confirm filter value"
            >
              <CheckIcon fontSize="small" />
            </button>
          );
        })()}
    </>
  );
}
